/**
 * Virtual delivery vocabulary and the one link rule (virtual-sessions-finish,
 * design D3 and D4).
 *
 * Pure and zero-import, so the browser barrel can export it: the webapp's
 * editors, the API's write doors and the worker's renderers share one platform
 * list, one display-name map and one link rule.
 *
 * SECURITY: the link and passcode are meeting credentials. Nothing here logs,
 * and refusals never echo the submitted value.
 */

/**
 * The fixed platform list, in the order every editor offers it. The keys are
 * the Prisma `VirtualPlatform` enum's values, so a stray string fails closed at
 * the database as well as here.
 */
export const VIRTUAL_PLATFORMS = [
  'ZOOM',
  'GOOGLE_MEET',
  'MICROSOFT_TEAMS',
  'FACETIME',
  'WEBEX',
  'OTHER',
] as const;

export type VirtualPlatformKey = (typeof VIRTUAL_PLATFORMS)[number];

const PLATFORM_LABELS: Record<VirtualPlatformKey, string | null> = {
  ZOOM: 'Zoom',
  GOOGLE_MEET: 'Google Meet',
  MICROSOFT_TEAMS: 'Microsoft Teams',
  FACETIME: 'FaceTime',
  WEBEX: 'Webex',
  // Other has no name: its phrases degrade to "Online" and "Join online".
  OTHER: null,
};

export function isVirtualPlatformKey(value: unknown): value is VirtualPlatformKey {
  return typeof value === 'string' && (VIRTUAL_PLATFORMS as readonly string[]).includes(value);
}

/**
 * The display name of a stored platform key, or null for Other, an unknown
 * value or no platform. Every renderer prints through this, never the key.
 */
export function virtualPlatformLabel(key: string | null | undefined): string | null {
  return isVirtualPlatformKey(key) ? PLATFORM_LABELS[key] : null;
}

export const VIRTUAL_LINK_MAX_LENGTH = 2000;
export const VIRTUAL_PASSCODE_MAX_LENGTH = 100;

/**
 * `template`: a service or an event template. A platform with no link is a
 * draft and saves; completeness is enforced at go-live and scheduling.
 *
 * `replacement`: a 1:1 session's or a single event date's own delivery. It
 * always carries a link when it names a platform; all three null means "use
 * the template".
 */
export type VirtualLinkRuleMode = 'template' | 'replacement';

export interface VirtualTripleInput {
  virtualPlatform?: string | null;
  virtualLink?: string | null;
  virtualPassword?: string | null;
}

export interface VirtualTriple {
  virtualPlatform: VirtualPlatformKey | null;
  virtualLink: string | null;
  virtualPassword: string | null;
}

export type VirtualLinkRuleCode =
  | 'VIRTUAL_PLATFORM_INVALID'
  | 'VIRTUAL_LINK_INVALID'
  | 'VIRTUAL_LINK_TOO_LONG'
  | 'VIRTUAL_LINK_MISSING'
  | 'VIRTUAL_PASSCODE_TOO_LONG'
  | 'VIRTUAL_PASSCODE_WITHOUT_LINK';

export type VirtualLinkRuleResult =
  | { ok: true; value: VirtualTriple }
  | {
      ok: false;
      field: keyof VirtualTriple;
      code: VirtualLinkRuleCode;
      message: string;
    };

export const VIRTUAL_LINK_RULE_MESSAGES: Record<VirtualLinkRuleCode, string> = {
  VIRTUAL_PLATFORM_INVALID: 'Choose a platform from the list.',
  VIRTUAL_LINK_INVALID: 'Enter a full meeting link starting with https://',
  VIRTUAL_LINK_TOO_LONG: `The meeting link can be at most ${VIRTUAL_LINK_MAX_LENGTH} characters.`,
  VIRTUAL_LINK_MISSING: 'Add the meeting link.',
  VIRTUAL_PASSCODE_TOO_LONG: `The passcode can be at most ${VIRTUAL_PASSCODE_MAX_LENGTH} characters.`,
  VIRTUAL_PASSCODE_WITHOUT_LINK: 'Add the meeting link.',
};

function trimToNull(value: string | null | undefined): string | null {
  if (value === null || value === undefined) return null;
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

/**
 * True for an absolute http or https URL. The Join button is a Telegram URL
 * button, which accepts only http, https and tg links, so app schemes such as
 * `zoommtg:` and `msteams:` would make Telegram refuse the whole message.
 */
export function isHttpMeetingLink(link: string): boolean {
  try {
    const url = new URL(link);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

function refuse(field: keyof VirtualTriple, code: VirtualLinkRuleCode): VirtualLinkRuleResult {
  return { ok: false, field, code, message: VIRTUAL_LINK_RULE_MESSAGES[code] };
}

/**
 * The one link rule at every door. Takes the EFFECTIVE triple (a partial PATCH
 * merges the stored values first; an absent field reads as null) and returns
 * the normalized triple to store, or the first refusal.
 */
export function checkVirtualLinkRule(
  input: VirtualTripleInput,
  mode: VirtualLinkRuleMode,
): VirtualLinkRuleResult {
  const platformRaw = trimToNull(input.virtualPlatform);
  const virtualLink = trimToNull(input.virtualLink);
  const virtualPassword = trimToNull(input.virtualPassword);

  if (platformRaw !== null && !isVirtualPlatformKey(platformRaw)) {
    return refuse('virtualPlatform', 'VIRTUAL_PLATFORM_INVALID');
  }
  const virtualPlatform = platformRaw as VirtualPlatformKey | null;

  if (virtualLink !== null) {
    if (virtualLink.length > VIRTUAL_LINK_MAX_LENGTH) return refuse('virtualLink', 'VIRTUAL_LINK_TOO_LONG');
    if (!isHttpMeetingLink(virtualLink)) return refuse('virtualLink', 'VIRTUAL_LINK_INVALID');
  }
  if (virtualPassword !== null) {
    if (virtualPassword.length > VIRTUAL_PASSCODE_MAX_LENGTH) {
      return refuse('virtualPassword', 'VIRTUAL_PASSCODE_TOO_LONG');
    }
    if (virtualLink === null) return refuse('virtualLink', 'VIRTUAL_PASSCODE_WITHOUT_LINK');
  }
  if (mode === 'replacement' && virtualPlatform !== null && virtualLink === null) {
    return refuse('virtualLink', 'VIRTUAL_LINK_MISSING');
  }

  return { ok: true, value: { virtualPlatform, virtualLink, virtualPassword } };
}

/**
 * Whether a message or page prints "Passcode: <code>": a passcode is stored and
 * the link does not already carry it verbatim (the editor hint asks admins to
 * leave the field empty when the passcode is inside the link; this is the
 * backstop).
 */
export function shouldShowPasscodeLine(
  link: string | null | undefined,
  passcode: string | null | undefined,
): boolean {
  const code = trimToNull(passcode);
  if (code === null || !link) return false;
  return !link.includes(code);
}

/**
 * Paid events are not online (paid-events-not-online, Gabriel
 * 2026-10-14). Telegram requires digital goods sold in a Mini App to go through
 * Stars, and Apple treats a paid online group event as one; a live 1:1 session
 * is a person-to-person service and is not affected. So an event template, a
 * single event date or a series may be virtual only while its payment setting
 * is Free. Unknown or missing reads as paid.
 */
export const PAID_VIRTUAL_EVENT_REASON =
  "Paid events can't be online. Apple treats a paid online event in the app as a digital purchase and takes 30%.";

export function eventVirtualAllowed(paymentRequirement: string | null | undefined): boolean {
  return paymentRequirement === 'NONE';
}
